import { Button } from "@/components/ui/button";
import { Navigation, MapPinned, Play, Timer, CheckCircle, Banknote, RotateCcw } from "lucide-react";
import WaitTimer from "@/components/WaitTimer";
import { phaseOf, liveWait } from "@/lib/tripPhase";

const big = "w-full h-14 font-bold text-lg";
const primary = `${big} bg-lime-400 hover:bg-lime-500 text-slate-900`;
const secondary = `${big} bg-slate-800 hover:bg-slate-700 border border-cyan-400/40 text-cyan-300`;

export const TripControls = ({ ride, isLoading, onAction, onNavigate, onCashReceived }) => {
  const phase = phaseOf(ride);
  const pickup = ride.pickup_locations?.[0] || {};
  const isCash = ride.payment_method === "cash";
  const cashPending = isCash && ride.payment_status !== "cash_received";
  const extraCash = isCash ? liveWait(ride).charge : 0;

  const navPickup = () => onNavigate(pickup.address, pickup.latitude, pickup.longitude);
  const navDropoff = () => onNavigate(ride.dropoff_address, ride.dropoff_latitude, ride.dropoff_longitude);

  return (
    <div className="space-y-3" data-testid="trip-controls">
      {["arrived", "on_trip", "waiting"].includes(phase) && <WaitTimer ride={ride} />}

      {phase === "en_route_pickup" && (
        <>
          <Button onClick={navPickup} className={secondary} data-testid="trip-navigate-pickup-btn">
            <Navigation className="w-5 h-5 mr-2" /> NAVIGATE TO PICKUP
          </Button>
          <Button onClick={() => onAction("arrived")} disabled={isLoading} className={primary} data-testid="mark-arrived-button">
            <MapPinned className="w-5 h-5 mr-2" /> I'VE ARRIVED
          </Button>
        </>
      )}

      {phase === "arrived" && (
        <>
          {cashPending && (
            <Button onClick={onCashReceived} disabled={isLoading} className={`${big} bg-amber-500 hover:bg-amber-600 text-slate-900`} data-testid="trip-cash-received-btn">
              <Banknote className="w-5 h-5 mr-2" /> CASH RECEIVED ${(ride.fare_estimate || 0).toFixed(2)}
            </Button>
          )}
          <Button onClick={() => onAction("start-trip")} disabled={isLoading || cashPending} className={primary} data-testid="trip-start-trip-btn">
            <Play className="w-5 h-5 mr-2" /> START TRIP
          </Button>
        </>
      )}

      {phase === "on_trip" && (
        <>
          <Button onClick={navDropoff} className={secondary} data-testid="trip-navigate-dropoff-btn">
            <Navigation className="w-5 h-5 mr-2" /> NAVIGATE TO DROPOFF
          </Button>
          <Button onClick={() => onAction("start-waiting")} disabled={isLoading} className={`${big} bg-amber-500/90 hover:bg-amber-500 text-slate-900`} data-testid="trip-start-waiting-btn">
            <Timer className="w-5 h-5 mr-2" /> START WAITING
          </Button>
        </>
      )}

      {phase === "waiting" && (
        <Button onClick={() => onAction("resume-trip")} disabled={isLoading} className={secondary} data-testid="trip-resume-trip-btn">
          <RotateCcw className="w-5 h-5 mr-2" /> RESUME TRIP
        </Button>
      )}

      {["on_trip", "waiting"].includes(phase) && (
        <>
          {extraCash > 0 && (
            <p className="text-center text-amber-300 font-semibold" data-testid="trip-extra-cash-due">
              Collect extra cash for wait time: ${extraCash.toFixed(2)}
            </p>
          )}
          <Button onClick={() => onAction("complete")} disabled={isLoading} className={primary} data-testid="complete-ride-button">
            <CheckCircle className="w-5 h-5 mr-2" /> COMPLETE TRIP
          </Button>
        </>
      )}
    </div>
  );
};

export default TripControls;
